import type { Offer, OfferType, PaymentProduct, PaymentTerm, Terms, TermsById, TitlingLender } from '@makemydeal/dr-platform-types';
import type { OfferReduxBaseState } from '../types/offerReduxBaseState';

import type { AutoTermIdOptions } from '../types/commonTypes';
import type { OfferTermOffer } from '../types/offerTypes';

import { objectHasProperty } from './helpers';
import {
    getCurrentOfferType,
    getDownPayment,
    getInitialPaymentDiscount,
    getOffer,
    getTerm,
    getTermIdByOfferType,
    getTitlingLenderWithAddressByOfferType
} from './offerNodeSelectors';
import { getOutOfPocket } from './selectedTermSelectors';
import { getTermIdIdWithExtension, parseOptionalNumber } from './utils/nonSelectorUtils';

export const getDeviceType = (state: OfferReduxBaseState) => {
    return getOffer(state).deviceType || '';
};

export const getSessionId = (state: OfferReduxBaseState) => {
    return getOffer(state).sessionId;
};

export const getShiftSessionId = (state: OfferReduxBaseState) => {
    return getOffer(state).shiftSessionId;
};

export const getUdCorrelationId = (state: OfferReduxBaseState) => {
    return getOffer(state).udCorrelationId;
};

export const getDealXgAutoSelectProgramSavedAt = (state: OfferReduxBaseState): string | undefined => {
    return getOffer(state).dealXgDetails?.autoSelectProgramSavedAt;
};

export const getDealRefId = (state: OfferReduxBaseState) => {
    return getOffer(state).dealRefId;
};

export const getDealRefIdFS = (state: OfferReduxBaseState) => {
    return getOffer(state).deal_ref_id;
};

export const getDealXgId = (state: OfferReduxBaseState): string | undefined => {
    return getOffer(state).dealXgId;
};

export const getIsDealXgRecordMissed = (state: OfferReduxBaseState): boolean => {
    const dealXgId = getDealXgId(state);
    return !dealXgId?.trim();
};

export const getDealXgVersion = (state: OfferReduxBaseState): string | undefined => {
    return getOffer(state).dealXgVersion;
};

export const getGlobalCustomerId = (state: OfferReduxBaseState) => {
    return getOffer(state).globalCustomerId;
};

export const getFsDealRefId = (state: OfferReduxBaseState) => {
    return getOffer(state).fsDealRefId;
};

export const getCreditAppId = (state: OfferReduxBaseState) => {
    return getOffer(state).creditAppId;
};

export const getOfferSource = (state: OfferReduxBaseState) => {
    return getOffer(state).offerSource || '';
};

export const isOfferFromVinSource = (state: OfferReduxBaseState) => {
    const source = getOfferSource(state) || '';
    return source.toLowerCase() === 'dealer';
};

export const getCreditAppCoApplicantEmail = (state: OfferReduxBaseState) => {
    return getOffer(state).creditApp?.coApplicantEmail || '';
};

export const getCmdMessageToShopper = (state: OfferReduxBaseState) => {
    return getOffer(state).cmdMessageToShopper;
};

export const getCmdMessageToSalesperson = (state: OfferReduxBaseState) => {
    return getOffer(state).cmdMessageToSalesperson;
};

export const getLeaseMileage = (state: OfferReduxBaseState): number | undefined => {
    return getOffer(state).leaseMileage;
};

export const getAutoTermId = (
    state: OfferReduxBaseState,
    termMonth: number,
    options: AutoTermIdOptions = {}
): string | undefined => {
    const type = options.offerType || getCurrentOfferType(state);

    options.getPairs ??= true;
    options.extension ??= {};

    if (!objectHasProperty(options.extension, 'DOWNPAYMENT')) {
        options.extension.DOWNPAYMENT =
            getDownPayment(state, type, options.pencilInputs) || getOutOfPocket(state, type, options.paymentTerm);
    }
    if (!objectHasProperty(options.extension, 'FORDFLEXBUY')) {
        options.extension.FORDFLEXBUY = getInitialPaymentDiscount(state, options.paymentTerm);
    }

    return getTermIdIdWithExtension(type, termMonth, options.extension, { getPairs: options.getPairs, termIdWithExtention: true });
};

// must move under terms selectors
export const getPaymentProducts = (state: OfferReduxBaseState, offerType: OfferType): PaymentProduct[] | undefined => {
    const termId = getTermIdByOfferType(state, offerType);
    const selectedTerm = getTerm(state, termId!);
    return selectedTerm?.products;
};

export const getCapCostReduction = (state: OfferReduxBaseState, offerType: OfferType): number | undefined => {
    const termId = getTermIdByOfferType(state, offerType);
    const selectedTerm = getTerm(state, termId!);
    return parseOptionalNumber(selectedTerm?.capCostReduction);
};

export const getIsTemplateApplied = (state: OfferReduxBaseState): boolean | undefined => {
    return getOffer(state).template?.isTemplateApplied;
};

export const getTemplateName = (state: OfferReduxBaseState): string => {
    return getOffer(state).template?.name || '';
};

export const getIsTemplateWithPaymentTerms = (state: OfferReduxBaseState): boolean | undefined => {
    return getOffer(state).template?.withPaymentTerms;
};

export const getOfferTermsDetails = (offer: Offer | OfferTermOffer): Terms => {
    return offer.terms || ({} as Terms);
};

export const getOfferTermsAllIds = (offer: OfferTermOffer): string[] => {
    return getOfferTermsDetails(offer).allIds || [];
};

export const getOfferFinanceTerms = (offer: OfferTermOffer): string[] => {
    return getOfferTermsDetails(offer).financeTerms || [];
};

export const getOfferLeaseTerms = (offer: OfferTermOffer): string[] => {
    return getOfferTermsDetails(offer).leaseTerms || [];
};

export const getTermsByIdPayments = (offer: Offer | OfferTermOffer): TermsById => {
    return getOfferTermsDetails(offer).byId || {};
};

export const getTermsByIdPayment = (offer: Offer | OfferTermOffer, termId?: string): PaymentTerm | undefined => {
    if (termId == null) {
        return undefined;
    }
    const termsById = getTermsByIdPayments(offer);
    return termsById[termId];
};

export const getVehicleFulfillment = (state: OfferReduxBaseState) => {
    return getOffer(state).vehicleFulfillment || {};
};

// Raw (non-coalesced) accessor: preserves an explicit `null` set on a vehicle switch so the
// offer-save payload can emit the DealXG delete signal. Use getVehicleFulfillment for reads.
export const getVehicleFulfillmentRaw = (state: OfferReduxBaseState) => {
    return getOffer(state).vehicleFulfillment;
};

export const getParticipantsSPD = (state: OfferReduxBaseState) => {
    return getOffer(state).participantsSPD || [];
};

export const getInsuranceVerifiedOn = (state: OfferReduxBaseState): string | undefined => {
    return getOffer(state).vehicleInsurance?.verifiedOn;
};

export const getContractSignedDate = (state: OfferReduxBaseState): string | undefined => {
    return getOffer(state).dealLifecycle?.contractSignedDate;
};

export const getTitlingLenderWithAddress = (state: OfferReduxBaseState): TitlingLender | undefined => {
    const offerType = getCurrentOfferType(state);
    return getTitlingLenderWithAddressByOfferType(state, offerType);
};
